import { useCallback, useContext, useState } from "react";
import { ApiContext } from "../../../context/MyContext";
import { post as commsPost, patch as commsPatch } from "../../../service/comms/commsApi";
import {
  assignTicket,
  escalateTicket,
  mergeTicket,
  patchTicket,
  reopenTicket,
  resolveTicket,
  snoozeTicket,
  unassignTicket,
} from "../../../service/comms/supportService";
import { requestSupportResync } from "../../../hooks/comms/RoomsUnreadProvider";
import { extractTicketDetail, getAssigneeId } from "./ticketUtils";

/**
 * Every ticket action for the currently open ticket, with toast feedback.
 *
 * Each success calls `onAfter` so the caller can refresh detail + queue, and
 * passes an optimistic partial ticket where the outcome is knowable up front
 * (pick-up flips the header to "assigned", snooze stamps `snoozed_until`) so
 * the UI doesn't wait on the ms-agents read model catching up.
 *
 * The escalate / snooze / merge / unassign actions take their input from
 * `TicketActionModal` rather than `window.prompt` — a browser dialog blocks
 * the event loop, and with it every Centrifugo publication handler, for as
 * long as it is open.
 *
 * @param {string} [roomId]
 * @param {(optimisticPartial?: Object) => void} [onAfter]
 * @returns {{
 *   toast: { message: string, type: string, id: number }|null,
 *   busy: string|null,
 *   resolving: boolean,
 *   assigning: boolean,
 *   handleEscalate: (payload?: Object) => Promise<void>,
 *   handleResolve: () => Promise<void>,
 *   handleReopen: () => Promise<void>,
 *   handlePickUp: () => Promise<void>,
 *   handleUnassign: (payload?: Object) => Promise<void>,
 *   handleSnooze: (payload: Object) => Promise<void>,
 *   handleUnsnooze: () => Promise<void>,
 *   handleMerge: (payload: Object) => Promise<void>,
 *   handlePatch: (payload: Object) => Promise<void>,
 * }}
 */
export function useTicketActions(roomId, onAfter) {
  const { empId } = useContext(ApiContext);
  const [toast, setToast] = useState(null);
  // Which action is in flight, so each button can disable only itself.
  const [busy, setBusy] = useState(null);

  const notify = useCallback(
    (message, type) => setToast({ message, type, id: Date.now() }),
    []
  );

  /**
   * Shared action shell: guard on roomId, flag busy, run, toast, resync and
   * hand an optimistic partial back to the caller.
   *
   * Rethrows so a modal submit can keep itself open on failure.
   *
   * @param {string} key - Action name, exposed as `busy`.
   * @param {() => Promise<*>} run
   * @param {{ success: string, failure: string, optimistic?: Object }} messages
   */
  const runAction = useCallback(
    async (key, run, { success, failure, optimistic }) => {
      if (!roomId) return;
      setBusy(key);
      try {
        const result = await run();
        notify(success, "success");
        requestSupportResync();
        onAfter?.(typeof optimistic === "function" ? optimistic(result) : optimistic);
      } catch (err) {
        notify(err?.message || failure, "error");
        throw err;
      } finally {
        setBusy(null);
      }
    },
    [roomId, notify, onAfter]
  );

  const handleResolve = useCallback(
    () =>
      runAction("resolve", () => resolveTicket(commsPost, roomId), {
        success: "Ticket resolved",
        failure: "Unable to resolve ticket",
        optimistic: { status: "resolved", ticket_status: "RESOLVED" },
      }).catch(() => {}),
    [runAction, roomId]
  );

  const handleReopen = useCallback(
    () =>
      runAction("reopen", () => reopenTicket(commsPost, roomId), {
        success: "Ticket reopened",
        failure: "Unable to reopen ticket",
        optimistic: { status: "active", ticket_status: "OPEN", resolved_at: null },
      }).catch(() => {}),
    [runAction, roomId]
  );

  const handleEscalate = useCallback(
    (payload = {}) =>
      runAction("escalate", () => escalateTicket(commsPost, roomId, payload), {
        success: "Ticket escalated",
        failure: "Unable to escalate ticket",
      }),
    [runAction, roomId]
  );

  // Self-assign: omitting the agent id makes ms-agents assign the caller.
  const handlePickUp = useCallback(
    () =>
      runAction("pickup", () => assignTicket(commsPatch, roomId), {
        success: "Ticket assigned to you",
        failure: "Unable to pick up ticket",
        // Prefer the PATCH response's ticket; if it carries no assignee (or no
        // body), overlay the viewer id so "Pick up" clears immediately even
        // when the read model lags.
        optimistic: (updated) => {
          const ticket = extractTicketDetail(updated) || {};
          return getAssigneeId(ticket) == null && empId != null
            ? { ...ticket, assignee_user_id: empId, assigned_agent_id: empId }
            : ticket;
        },
      }).catch(() => {}),
    [runAction, roomId, empId]
  );

  const handleUnassign = useCallback(
    (payload = {}) =>
      runAction("unassign", () => unassignTicket(commsPost, roomId, payload.reason), {
        success: "Returned to the pool",
        failure: "Unable to return the ticket",
        optimistic: {
          assignee_user_id: null,
          assigned_agent_id: null,
          assignee_name: null,
        },
      }),
    [runAction, roomId]
  );

  const handleSnooze = useCallback(
    (payload) =>
      runAction("snooze", () => snoozeTicket(commsPost, roomId, payload), {
        success: "Ticket snoozed",
        failure: "Unable to snooze ticket",
        optimistic: { snoozed_until: payload?.until, snooze_reason: payload?.reason },
      }),
    [runAction, roomId]
  );

  const handleUnsnooze = useCallback(
    () =>
      runAction("snooze", () => snoozeTicket(commsPost, roomId, { until: null }), {
        success: "Back in the queue",
        failure: "Unable to wake the ticket",
        optimistic: { snoozed_until: null, snooze_reason: null },
      }).catch(() => {}),
    [runAction, roomId]
  );

  /**
   * Merge two tickets. `sourceRoomId` is the one being merged *away* — usually
   * the open ticket, but the agent may decide the other one survives, and the
   * API is always called on the loser.
   */
  const handleMerge = useCallback(
    ({ sourceRoomId, ...payload }) => {
      const loser = sourceRoomId || roomId;
      const losingOpenTicket = String(loser) === String(roomId);
      return runAction("merge", () => mergeTicket(commsPost, loser, payload), {
        success: "Tickets merged",
        failure: "Unable to merge tickets",
        // Only stamp the open ticket when it is the one that closed.
        optimistic: losingOpenTicket
          ? { merged_into_room_id: payload?.target_room_id, ticket_status: "CLOSED" }
          : undefined,
      });
    },
    [runAction, roomId]
  );

  /**
   * Edit priority / department / type / subject / tags. `tags` replaces the
   * whole set server-side, so callers must send the full list.
   */
  const handlePatch = useCallback(
    (payload) =>
      runAction("patch", () => patchTicket(commsPatch, roomId, payload), {
        success: "Ticket updated",
        failure: "Unable to update ticket",
        optimistic: payload,
      }),
    [runAction, roomId]
  );

  return {
    toast,
    busy,
    // Kept for the existing call sites that only care about these two.
    resolving: busy === "resolve",
    assigning: busy === "pickup",
    handleEscalate,
    handleResolve,
    handleReopen,
    handlePickUp,
    handleUnassign,
    handleSnooze,
    handleUnsnooze,
    handleMerge,
    handlePatch,
  };
}
